import { useState } from "react";
import axios from "axios";
import { PUBLIC_PAGES, isReservedPath } from "../sitePages";

// Same charset rule the attribution capture uses (attribution.js VALID, server UTM_RE)
const VALID = /^[a-z0-9._-]{1,100}$/;
const PLATFORM_SUGGESTIONS = ["youtube", "reddit", "instagram", "discord", "email", "podcast"];
const MEDIUM_DEFAULTS = { youtube: "video", reddit: "social", instagram: "social", discord: "social", email: "email" };
const ABBREV = { youtube: "yt", reddit: "rd", instagram: "ig", discord: "dc", email: "em", podcast: "pod" };
const PROD_DOMAIN = (process.env.REACT_APP_PRODUCTION_DOMAIN || "").trim().replace(/\/+$/, "");
const EMPTY = { platform: "", campaign: "", destination: "/free-book", medium: "", path: "" };

const mediumFor = (platform) => {
  const p = platform.trim().toLowerCase();
  if (!p) return "";
  return MEDIUM_DEFAULTS[p] || "referral";
};

const suggestPath = (platform, campaign, destination) => {
  const p = platform.trim().toLowerCase();
  const c = campaign.trim().toLowerCase();
  if (!p || !c) return "";
  const ab = ABBREV[p] || p.slice(0, 2);
  const tail = c.startsWith(`${p}-`) ? c.slice(p.length + 1) : c;
  return `${destination === "/" ? "" : destination}/${ab}-${tail}`;
};

export const CampaignLinkBuilder = ({ api, csrf, redirects, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState({ medium: false, path: false });
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(null);
  const [copied, setCopied] = useState(false);

  const update = (patch, touch) => {
    setError("");
    const touchedNext = { ...touched, ...(touch || {}) };
    if (touch) setTouched(touchedNext);
    setForm((f) => {
      const next = { ...f, ...patch };
      if (!touchedNext.medium) next.medium = mediumFor(next.platform);
      if (!touchedNext.path) next.path = suggestPath(next.platform, next.campaign, next.destination);
      return next;
    });
  };

  const platform = form.platform.trim().toLowerCase();
  const campaign = form.campaign.trim().toLowerCase();
  const medium = form.medium.trim().toLowerCase();
  const path = form.path.trim().toLowerCase();
  const shareBase = PROD_DOMAIN || window.location.origin;
  const destinationUrl = `${form.destination}?utm_source=${platform}${medium ? `&utm_medium=${medium}` : ""}&utm_campaign=${campaign}`;
  const showPreview = platform && campaign && path;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!platform) return setError("Platform is required.");
    if (!VALID.test(platform)) return setError("Platform may only contain letters, numbers, dots, underscores and hyphens (max 100 characters).");
    if (!campaign) return setError("Campaign is required.");
    if (!VALID.test(campaign)) return setError("Campaign may only contain letters, numbers, dots, underscores and hyphens (max 100 characters).");
    if (medium && !VALID.test(medium)) return setError("Medium may only contain letters, numbers, dots, underscores and hyphens (max 100 characters).");
    if (!PUBLIC_PAGES.includes(form.destination)) return setError("Destination must be a public page on this site.");
    if (!path.startsWith("/")) return setError("Link path must start with '/'.");
    if (/\s/.test(path)) return setError("Link path cannot contain whitespace.");
    if (isReservedPath(path)) return setError(`${path} is an existing site page — the link would never redirect.`);
    const dup = redirects.find((r) => r.source.toLowerCase() === path);
    if (dup) return setError(`Link path ${path} collides with the existing redirect ${dup.source} → ${dup.destination}.`);
    try {
      const res = await axios.post(
        `${api}/redirects`,
        { source: path, destination: destinationUrl, type: 302 },
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      onCreated(res.data);
      setSaved({ url: `${shareBase}${path}`, isPreview: !PROD_DOMAIN });
      setCopied(false);
      setForm(EMPTY);
      setTouched({ medium: false, path: false });
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed. Log out and back in, then try again.");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(saved.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <form onSubmit={submit} className="border border-hairline p-6 mb-6" data-testid="campaign-link-form">
      <p className="label-mono text-ink mb-6">New campaign link</p>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
        <div className="md:col-span-3">
          <label className="label-mono text-muted block mb-2">Platform</label>
          <input
            type="text"
            list="campaign-platform-suggestions"
            value={form.platform}
            onChange={(e) => update({ platform: e.target.value })}
            placeholder="youtube"
            className="w-full bg-transparent border-b border-ink py-2 outline-none"
            data-testid="campaign-link-platform"
          />
          <datalist id="campaign-platform-suggestions">
            {PLATFORM_SUGGESTIONS.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </div>
        <div className="md:col-span-3">
          <label className="label-mono text-muted block mb-2">Campaign</label>
          <input
            type="text"
            value={form.campaign}
            onChange={(e) => update({ campaign: e.target.value })}
            placeholder="launch-video"
            className="w-full bg-transparent border-b border-ink py-2 outline-none"
            data-testid="campaign-link-campaign"
          />
        </div>
        <div className="md:col-span-3">
          <label className="label-mono text-muted block mb-2">Destination page</label>
          <select
            value={form.destination}
            onChange={(e) => update({ destination: e.target.value })}
            className="w-full bg-transparent border-b border-ink py-2 outline-none label-mono"
            data-testid="campaign-link-destination"
          >
            {PUBLIC_PAGES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-3">
          <label className="label-mono text-muted block mb-2">Medium (optional)</label>
          <input
            type="text"
            value={form.medium}
            onChange={(e) => update({ medium: e.target.value }, { medium: true })}
            placeholder="video"
            className="w-full bg-transparent border-b border-ink py-2 outline-none"
            data-testid="campaign-link-medium"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end mt-4">
        <div className="md:col-span-9">
          <label className="label-mono text-muted block mb-2">Link path</label>
          <input
            type="text"
            value={form.path}
            onChange={(e) => update({ path: e.target.value }, { path: true })}
            placeholder="/free-book/yt-launch"
            className="w-full bg-transparent border-b border-ink py-2 outline-none"
            data-testid="campaign-link-path"
          />
        </div>
        <div className="md:col-span-3 flex justify-end">
          <button
            type="submit"
            className="label-mono bg-ink text-white px-4 py-2 hover:bg-black transition-colors"
            data-testid="campaign-link-submit"
          >
            Create link
          </button>
        </div>
      </div>
      {showPreview && (
        <div className="mt-6 border-t border-hairline pt-4 grid grid-cols-1 md:grid-cols-12 gap-2 items-baseline">
          <div className="md:col-span-3 label-mono text-muted">Shareable link</div>
          <div className="md:col-span-9 label-mono text-ink break-all" data-testid="campaign-link-preview-share">{shareBase}{path}</div>
          <div className="md:col-span-3 label-mono text-muted">Redirects to</div>
          <div className="md:col-span-9 text-sm text-ink break-all" data-testid="campaign-link-preview-destination">{destinationUrl}</div>
        </div>
      )}
      {error && (
        <p className="mt-4 text-sm text-red-600" data-testid="campaign-link-error">{error}</p>
      )}
      {saved && (
        <div className="mt-6 border-t border-hairline pt-4" data-testid="campaign-link-saved">
          <div className="flex flex-wrap items-center gap-3">
            <span className="label-mono text-muted">Link created</span>
            <span className="label-mono text-ink break-all" data-testid="campaign-link-saved-url">{saved.url}</span>
            <button
              type="button"
              onClick={copy}
              className="label-mono border border-hairline px-3 py-1.5 hover:bg-ink hover:text-white transition-colors"
              data-testid="campaign-link-copy"
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          {saved.isPreview && (
            <p className="mt-3 text-sm text-amber-700" data-testid="campaign-link-preview-warning">
              No production domain is configured — this link points at the preview domain and must not be shared publicly.
            </p>
          )}
        </div>
      )}
    </form>
  );
};
